import {
	Button,
	InfoCard,
	useUnits,
	UnitField,
	HTMLInputElementExtended,
	HTMLTextAreaElementExtended,
	useIsFeatureEnabled,
	SecondaryNav,
	CancellableEvent,
	WarningDialog,
	error,
} from '@innovyze/stylovyze';
import {
	CachedField,
	Category,
	Component,
	ComponentCount,
	ComponentField,
	ComponentMetadata,
	ComponentType,
	MultiParamCell,
	MultiScoreItem,
	ScoreDataGridRow,
	SimulationData,
	UnitMetadata,
	getTypeDisplay,
} from '@Types';
import { getObservationCodes } from '@innovyze/lib_am_common/Utils';
import { FullPage, FullPageWrapper } from '@Utils/styles';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import {
	addCoFComponent,
	addLoFComponent,
	getCoFComponentAssetMetadata,
	getLoFComponentAssetMetadata,
	getLoFComponentInspectionMetadata,
	setCoFComponent,
	setLoFComponent,
	setRiskScores,
	setRiskUnitScores,
	getLoFComponentProximityAttrMetadata,
	getCoFComponentProximityAttrMetadata,
	getLoFComponentCustomTableMetadata,
	getCoFComponentCustomTableMetadata,
	setRiskEditScores,
	changeSpatialProximity,
	setRiskScoreDataGrid,
	getRiskCountAttribute,
	getRiskCountProximityAttribute,
	getRiskCountProximity,
	getRiskCountCustomTable,
	getRiskCountInspection,
	clearComponentMetadata,
	renameRiskCount,
	setDataGridState,
	getRiskCountTask,
	getLoFComponentTaskMetadata,
	getCoFComponentTaskMetadata,
	getRiskCountResolved,
	setRiskCountIsLoading,
	getRiskCountScoreMatrix,
} from '@Actions';
import {
	selectAssetFieldsStore,
	selectCofStore,
	useComponentMetadataStore,
	selectLofStore,
	selectRiskCount,
	selectRiskScoreDataGrid,
	useSelectIsCountLoading,
	selectRiskStore,
	selectRiskMultiScoreEditDataGrid,
} from '@Selectors';
import { useGlobalization } from '@Translations/useGlobalization';
import {
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Autocomplete,
	createFilterOptions,
	Tooltip,
	Chip,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useStyles } from './AddEditComponent.styles';
import { MapInsertSpatial } from '@innovyze/lib_am_common/Components';
import { MapStatusType } from '@innovyze/lib_am_common/Types';
import ScoreDataGrid, { updateRiskCounts } from './ScoreDataGrid/ScoreDataGrid';
import { Stack } from '@mui/system';
import MultiScoreDataGrid from './MultiScoreDataGrid/MultiScoreDataGrid';
import ParameterDetails from './ParameterDetails/ParameterDetails';

import { getObservationFields } from '@Utils/observationFields';
import { TFunc } from '@Translations/types';
import { SpatialLayer } from '@innovyze/lib_am_common';
import { useWebSockets } from '@innovyze/lib_am_common/Utils';
import { WebSocketData } from '@Types/webSocket.types';

export const fixupTaskField = (t: TFunc, field: string): string => {
	switch (field) {
		case 'completedDate':
			return t('Completed Date');
		case 'scheduledDate':
			return t('Scheduled Date');
		case 'startChainage':
			return t('Start Chainage');
		case 'distance':
			return t('Distance');
		case 'direction':
			return t('Direction');
		default:
			return field;
	}
};

interface Props {
	countKey: string;
	assetType: string;
	forLoF: boolean;
	observations: boolean;
	inspectionType?: string;
	isNew: boolean;
	category?: Category;
	component: Component;
	parentComponent?: Component;
	parameterNumber?: number;
	onApply: (category: Category | undefined) => void;
	onCancel: () => void;
	onChange?: (
		changed: boolean,
		clearMatrix: boolean,
		nextTab: boolean,
	) => void;
}

interface RenderThirdFormControlProps {
	title: string;
	onChange: (event: SelectChangeEvent) => void;
	renderValues: () => JSX.Element[];
	defaultValue: string;
	valueWhenDisabled: string;
}

export const AddEditComponent = ({
	countKey,
	assetType,
	forLoF,
	observations,
	inspectionType,
	isNew,
	category,
	component,
	parentComponent,
	parameterNumber,
	onApply,
	onCancel,
	onChange,
}: Props) => {
	const DEFECT_CODE_TYPE = 'PACP_CODE';
	const isSimulationEnabled = useIsFeatureEnabled('info-360-am-simulations');
	const isRiskSimulationEnabled = useIsFeatureEnabled(
		'info-360-am-simulations-risk',
	);
	const isRiskTaskComponentEnabled = useIsFeatureEnabled(
		'info-360-risk-calc-tasks',
	);
	const isRiskCountTaskEnabled = useIsFeatureEnabled(
		'info-360-asset-risk-count-task',
	);
	const isWebSocketEnabled = useIsFeatureEnabled(
		'info-360-asset-risk-web-socket',
	);
	const isRiskPACPObservationsEnabled = useIsFeatureEnabled(
		'info-360-asset-risk-observations',
	);

	const { t } = useGlobalization();
	const classes = useStyles();
	const dispatch = useDispatch();
	const {
		getSystemValueNoIntlFormat,
		getStandardisedValue,
		getSystemUnit,
		system,
	} = useUnits();

	const changedRangesKey = `ChangedRanges${parameterNumber ?? ''}`;
	const lofStore = forLoF ? selectLofStore() : null;
	const cofStore = !forLoF ? selectCofStore() : null;
	const assetStore = selectAssetFieldsStore();
	const riskStore = selectRiskStore();

	const [componentName, setComponentName] = useState<string>(
		component ? component.name : '',
	);
	const [field, setField] = useState<string>(
		component && component.field ? component.field : '',
	);
	const [fieldAlias, setFieldAlias] = useState<string>(
		component && component.fieldAlias ? component.fieldAlias : '',
	);
	const [simulationName, setSimulationName] = useState<string>(
		(component && component.simulationName) ?? '',
	);
	const [taskName, setTaskName] = useState<string>(component?.taskName ?? '');
	const [uploadId, setUploadId] = useState<string>(component?.uploadId ?? '');

	const [simulationUploadId, setSimulationUploadId] = useState<string>(
		(component && component.simulationUploadId) ?? '',
	);
	const [type, setType] = useState<ComponentType>(
		component && component.type
			? (component.type as ComponentType)
			: ComponentType.Attribute,
	);

	const getTaskLayerName = (taskName: string, uploadId: string) =>
		`${uploadId}`;

	const [dataSource, setDataSource] = useState<string>(
		component && component.layer ? component.layer : '',
	);
	const [distance, setDistance] = useState<number>(
		component && component.maxDistance ? component.maxDistance : 0,
	);
	const [metadata, setMetadata] = useState<ComponentMetadata[] | undefined>(
		component && component.metadata ? component.metadata : undefined,
	);
	const [metadataType, setMetadataType] = useState<string>(
		component && component.metadataType ? component.metadataType : '',
	);
	const [nullScore, setNullScore] = useState<number>(
		component && component.nullScore ? component.nullScore : 0,
	);
	const [unitMetadata, setUnitMetadata] = useState<UnitMetadata | undefined>(
		component && component.unitMetadata
			? component.unitMetadata
			: undefined,
	);
	const [unitMetadataName, setUnitMetadataName] = useState<string>(
		component && component.unitMetadataName
			? component.unitMetadataName
			: '',
	);

	const [componentChanged, setComponentChanged] = useState<boolean>(false);

	const [validName, setValidName] = useState<string>('');
	const [validConfig, setValidConfig] = useState<boolean>(false);

	const [showScoreTable, setShowScoreTable] = useState<boolean>(false);

	const [isUnique, setIsUnique] = React.useState(true);
	const [rangeClasses, setRangeClasses] = React.useState<number>(
		component && component.metadata?.length
			? component.metadata.length -
					(parameterNumber !== undefined &&
					inspectionType === undefined
						? 3
						: 2)
			: 5,
	);
	const [hasMap, setHasMap] = React.useState(
		component &&
			(component.type === ComponentType.SpatialProximity ||
				component.type === ComponentType.SpatialAttribute),
	);

	const [rangeChanged, setRangeChanged] = React.useState(false);
	const [fetchingMetadata, setFetchingMetadata] = useState<boolean>(false);
	const [disableFieldSelect, setDisableFieldSelect] =
		useState<boolean>(false);
	const [selectedCachedField, setSelectedCachedField] =
		useState<CachedField | null>(null);

	const scoreRows = selectRiskScoreDataGrid();
	const componentMetadataStore = useComponentMetadataStore();
	const metadataStore = useMemo(() => {
		switch (type) {
			case ComponentType.Simulation: {
				return { isLoading: false, ...selectedCachedField };
			}
			default:
				return componentMetadataStore;
		}
	}, [type, selectedCachedField, componentMetadataStore]);

	const riskCounts = selectRiskCount(
		component.name.length !== 0 && rangeChanged
			? changedRangesKey
			: countKey,
	);
	const isRiskCountLoading = useSelectIsCountLoading(
		component.name.length !== 0 && rangeChanged
			? changedRangesKey
			: countKey,
	);
	const [requestedCountMetadata, setRequestedCountMetadata] =
		React.useState<ComponentMetadata[]>();

	const [showMutliParam, setShowMutliParam] = useState<boolean>(false);
	const [componentParameter1, setComponentParameter1] = useState<
		Component | undefined
	>(undefined);
	const [componentParameter2, setComponentParameter2] = useState<
		Component | undefined
	>(undefined);
	const [componentMatrix, setComponentMatrix] = useState<
		MultiScoreItem[] | undefined
	>(undefined);
	const [currentIndex, setCurrentIndex] = useState<number>(0);
	const [moveToIndex, setMoveToIndex] = useState<number | undefined>(
		undefined,
	);
	const multiScoreEdit = selectRiskMultiScoreEditDataGrid();

	// map medtadata to defect code list
	const [defects, setDefects] = useState<string[]>(
		component?.metadata?.map(item => item.key) || [],
	);

	// get defect code list by inspectionType
	const defectCodeList: { code: string; label: string }[] = useMemo(() => {
		if (field !== DEFECT_CODE_TYPE) return [];
		const inspectionStandard = inspectionType
			? inspectionType.toLocaleUpperCase()
			: 'PACP';
		const codes = getObservationCodes(inspectionStandard, t);
		return Object.keys(codes).map((key: string) => {
			return { code: key, label: codes[key] };
		});
	}, [type, field]);
	// filter for defect code auto complete
	const filterOptions = createFilterOptions({
		matchFrom: 'start',
	});

	const webSocket = useWebSockets();
	const riskCalCountCallBack = async (data: WebSocketData) => {
		console.log('riskCalcCount', data);

		const key = data.payload.key;
		const response = data.payload.payload;
		const requestId = data.requestId;
		const connectionId = data.connectionId;
		console.log('riskCalcCount', connectionId, requestId);

		console.log('dispatch', {
			key: key,
			values: response,
		});
		dispatch(
			getRiskCountResolved({
				key: key,
				values: response,
			}),
		);
		dispatch(
			setRiskCountIsLoading({
				key: key,
				loading: false,
			}),
		);
	};
	const riskCalCountErrorCallBack = async (data: WebSocketData) => {
		dispatch(
			setRiskCountIsLoading({
				key: data.requestBody.key,
				loading: false,
			}),
		);
	};
	webSocket.setupRoutes(
		'riskCalcCount',
		riskCalCountCallBack,
		riskCalCountErrorCallBack,
	);

	const clearFields = (changeType: boolean) => {
		if (!changeType) {
			setComponentName('');
			setValidName(t('Enter a valid name'));
			setType(ComponentType.Attribute);
			if (onChange) onChange(false, true, false);
		}
		dispatch(
			setRiskCountIsLoading({
				key: countKey,
				loading: false,
			}),
		);
		setField('');
		setFieldAlias('');
		setSelectedCachedField(null);
		setSimulationName('');
		setTaskName('');
		setUploadId('');
		setSimulationUploadId('');
		setDefects([]);

		setDistance(0);
		setDisableFieldSelect(false);
		dispatch(
			setDataGridState({
				key: countKey,
				initialState: undefined,
			}),
		);
	};

	const menuItemsInspectionLoF = useMemo(() => {
		return lofStore?.componentInspectionFields.map(
			(item: ComponentField, index: number) => (
				<MenuItem
					key={index}
					value={item.cachedField}
					disabled={!item.isCached}>
					{item.userFriendlyName}
				</MenuItem>
			),
		);
	}, [lofStore]);

	const menuItemsAssetLoF = useMemo(() => {
		return lofStore?.componentFields.map(
			(item: ComponentField, index: number) => (
				<MenuItem
					key={index}
					value={item.cachedField}
					disabled={!item.isCached}>
					{item.userFriendlyName}
				</MenuItem>
			),
		);
	}, [lofStore]);

	const menuItemsCoF = useMemo(() => {
		return cofStore?.componentFields.map(
			(item: ComponentField, index: number) => (
				<MenuItem
					key={index}
					value={item.cachedField}
					disabled={!item.isCached}>
					{item.userFriendlyName}
				</MenuItem>
			),
		);
	}, [cofStore]);

	const menuItemsType = useMemo(() => {
		const fieldTypes = [
			{
				value: ComponentType.Attribute,
				label: getTypeDisplay(ComponentType.Attribute, t),
			},
		];

		if (inspectionType === undefined) {
			if (isRiskSimulationEnabled && isSimulationEnabled) {
				fieldTypes.push({
					value: ComponentType.Simulation,
					label: getTypeDisplay(ComponentType.Simulation, t),
				});
			}

			if (isRiskTaskComponentEnabled) {
				fieldTypes.push({
					value: ComponentType.Task,
					label: getTypeDisplay(ComponentType.Task, t),
				});
			}

			fieldTypes.push(
				{
					value: ComponentType.SpatialProximity,
					label: getTypeDisplay(ComponentType.SpatialProximity, t),
				},
				{
					value: ComponentType.SpatialAttribute,
					label: getTypeDisplay(ComponentType.SpatialAttribute, t),
				},
				{
					value: ComponentType.CustomTable,
					label: getTypeDisplay(ComponentType.CustomTable, t),
				},
			);
		}

		if (isRiskPACPObservationsEnabled && forLoF && observations) {
			fieldTypes.push({
				value: ComponentType.Observation,
				label: getTypeDisplay(ComponentType.Observation, t),
			});
		}

		// Keep MultiParam at the bottom for consistency
		if (parameterNumber === undefined) {
			fieldTypes.push({
				value: ComponentType.MultiParam,
				label: getTypeDisplay(ComponentType.MultiParam, t),
			});
		}

		return fieldTypes.map(
			(item: { value: string; label: string }, index: number) => {
				return (
					<MenuItem key={index} value={item.value}>
						{item.label}
					</MenuItem>
				);
			},
		);
	}, [inspectionType, parameterNumber]);

	const menuItemsLayerList = useMemo(() => {
		if (
			assetStore.spatialLayerList &&
			assetStore.spatialLayerList.length > 0
		) {
			const layerList = assetStore.spatialLayerList.map(
				layer => layer.LAYER_NAME,
			);
			const uniqLayer = layerList
				.filter((item, index) => layerList.indexOf(item) === index)
				.sort((a, b) => {
					return a.localeCompare(b);
				});
			return uniqLayer.map((layer, index: number) => (
				<MenuItem key={index} value={layer}>
					{layer}
				</MenuItem>
			));
		} else {
			return [];
		}
	}, [type, assetStore, dataSource]);

	const menuItemsSpatialAttrList = useMemo(() => {
		if (
			assetStore.spatialProximityAttr &&
			assetStore.spatialProximityAttr.length > 0
		) {
			const layerList = assetStore.spatialProximityAttr.map(
				layer => layer.layerName,
			);
			const uniqLayer = layerList
				.filter((item, index) => layerList.indexOf(item) === index)
				.sort((a, b) => {
					return a.localeCompare(b);
				});
			return uniqLayer.map((layer, index: number) => (
				<MenuItem key={index} value={layer}>
					{layer}
				</MenuItem>
			));
		} else {
			return [];
		}
	}, [type, assetStore, dataSource]);

	const menuItemsSpatialAttrFieldList = useMemo(() => {
		if (
			assetStore.spatialProximityAttr &&
			assetStore.spatialProximityAttr.length > 0 &&
			dataSource != ''
		) {
			const fieldList = assetStore.spatialProximityAttr
				.filter(layer => layer.layerName == dataSource)
				.map(layer => layer.cachedField)
				.sort((a, b) => {
					return a.localeCompare(b);
				});
			return fieldList.map((field2, index: number) => (
				<MenuItem key={index} value={field2}>
					{field2}
				</MenuItem>
			));
		} else {
			return [];
		}
	}, [type, assetStore, dataSource]);

	const menuItemsCustomTableList = useMemo(() => {
		if (assetStore.customTable && assetStore.customTable.length > 0) {
			const tableList = assetStore.customTable.map(
				table => table.layerName,
			);
			const uniqTable = tableList
				.filter((item, index) => tableList.indexOf(item) === index)
				.sort((a, b) => {
					return a.localeCompare(b);
				});

			return uniqTable.map((table, index: number) => (
				<MenuItem key={index} value={table}>
					{table}
				</MenuItem>
			));
		} else {
			return [];
		}
	}, [type, assetStore, dataSource]);

	const menuItemsCustomTableFieldList = useMemo(() => {
		if (
			assetStore.customTable &&
			assetStore.customTable.length > 0 &&
			dataSource != ''
		) {
			const fieldList = assetStore.customTable
				.filter(layer => layer.layerName == dataSource)
				.sort((a, b) => {
					return a.cachedField.localeCompare(b.cachedField);
				});
			return fieldList.map((layer, index: number) => (
				<MenuItem key={index} value={layer.cachedField}>
					{layer.userFriendlyName}
				</MenuItem>
			));
		} else {
			return [];
		}
	}, [type, assetStore, dataSource]);

	const menuItemsObservationFieldList = useMemo(() => {
		if (type !== ComponentType.Observation) return [];
		return getObservationFields(t).map(
			(item: { value: string; label: string; disabled: boolean }) => (
				<MenuItem
					key={item.value}
					value={item.value}
					disabled={item.disabled}>
					{item.label}
				</MenuItem>
			),
		);
	}, [type]);

	const renderObservationFieldsForm = () => {
		if (isNew) {
			return (
				<FormControl variant="outlined" margin="normal" fullWidth>
					<InputLabel>{t('Field')}</InputLabel>
					<Select
						id={'fieldSelectOF'}
						key={`f${dataSource}${Date.now}`}
						data-cy="fieldField"
						name="field"
						defaultValue={field}
						disabled={isRiskCountLoading || disableFieldSelect}
						onChange={handleFieldSelectChangeObservation}
						label={t('Field')}>
						{menuItemsObservationFieldList}
					</Select>
				</FormControl>
			);
		} else {
			return (
				<FormControl variant="outlined" margin="normal" fullWidth>
					<TextField
						fullWidth
						data-cy="fieldField"
						name="field"
						label={t('Field')}
						variant="outlined"
						defaultValue={field}
						disabled={true}
					/>
				</FormControl>
			);
		}
	};

	const renderObservationCodeValueForm = () => {
		if (field === DEFECT_CODE_TYPE) {
			return (
				<FormControl variant="outlined" margin="normal" fullWidth>
					<InputLabel></InputLabel>
					<Autocomplete
						className={classes.defectRoot}
						classes={{
							tag: classes.codeListTag,
							option: classes.codeListOption,
						}}
						filterOptions={filterOptions}
						multiple
						id="defectCodes"
						freeSolo
						options={defectCodeList.map(defect => defect.code)}
						defaultValue={defects}
						getOptionLabel={option => {
							const defectcode = defectCodeList.find(
								code => code.code == option,
							);

							if (defectcode)
								return `${option} - ${defectcode.label}`;
							else return `${option}`;
						}}
						renderInput={params => (
							<TextField {...params} variant="outlined" />
						)}
						disabled={isRiskCountLoading || disableFieldSelect}
						onChange={handleObservationFieldInputChange}
						renderTags={(values, getTagProps) =>
							values.map((value, index) => (
								<Tooltip
									key={value as string}
									title={
										defectCodeList.find(
											code => code.code == value,
										)?.label ?? ''
									}>
									<Chip
										{...getTagProps({ index })}
										label={value as string}
									/>
								</Tooltip>
							))
						}
					/>
				</FormControl>
			);
		}
	};

	const handleFieldSelectChangeObservation = (
		event: SelectChangeEvent<string>,
	) => {
		const target = event?.target as HTMLInputElement;
		const observationfield = target.value;
		if (observationfield) {
			setField(observationfield);
			setFieldAlias(observationfield);
			setComponentChanged(true);
			if (onChange) onChange(true, false, false);
		}
	};

	const handleTypeSelectChange = (event: SelectChangeEvent<string>) => {
		const target = event?.target as HTMLInputElement;
		const type = target.value as ComponentType;

		setType(type);
		setValidConfig(false);
		setComponentChanged(true);
		if (onChange) onChange(true, false, false);
		clearFields(true);

		if (type === ComponentType.MultiParam) {
			const parameter1 = {
				name: '',
				select: false,
				weighting: 0,
				type: '',
				metadata: undefined,
				metadataType: undefined,
				maxDistance: undefined,
				field: undefined,
				fieldAlias: undefined,
				nullScore: undefined,
				parmeterComponent: undefined,
				scoreMatrix: undefined,
			};
			setComponentParameter1(parameter1);
			const parameter2 = {
				name: '',
				select: false,
				weighting: 0,
				type: '',
				metadata: undefined,
				metadataType: undefined,
				maxDistance: undefined,
				field: undefined,
				fieldAlias: undefined,
				nullScore: undefined,
				parmeterComponent: undefined,
				scoreMatrix: undefined,
			};
			setComponentParameter2(parameter2);
			setComponentMatrix(undefined);
			component.paramComponents = [parameter1, parameter2];
		} else if (componentParameter1) {
			setComponentParameter1(undefined);
			setComponentParameter2(undefined);
			setComponentMatrix(undefined);
			component.paramComponents = undefined;
		}
	};

	const handleDataSourceSelectChange = (event: SelectChangeEvent<string>) => {
		const target = event?.target as HTMLInputElement;
		setDataSource(target.value);
		setField('');
		setFieldAlias('');
		setDistance(0);
		setComponentChanged(true);
		if (onChange) onChange(true, false, false);
		const sl = assetStore.spatialLayerList.find(
			s => s.LAYER_NAME === target.value,
		);
		const slm: SpatialLayer = {
			_ID: sl?._ID ?? '',
			TENANT_ID: sl?.TENANT_ID ?? '',
			LAYER_NAME: sl?.LAYER_NAME ?? '',
			EXTENT: sl?.EXTENT ?? undefined,
			GEOMETRY_TYPE: sl?.GEOMETRY_TYPE ?? undefined,
		};
		dispatch(changeSpatialProximity(slm));
	};

	const handleFieldSelectChangeCustomTable = (
		event: SelectChangeEvent<string>,
	) => {
		if (assetStore) {
			const target = event?.target as HTMLInputElement;
			const result = assetStore.customTable.find(
				({ cachedField }) => cachedField === target.value,
			);
			if (result) {
				setField(result.cachedField);
				setFieldAlias(result.userFriendlyName);
				setComponentChanged(true);
				if (onChange) onChange(true, false, false);

				// This is a workaround to fix the MUI issue that disabling a select with focus does not correctly keep the focus when un-disabled
				setTimeout(() => {
					const field = document.getElementById('fieldSelectCT');
					if (field) field.blur();
					setDisableFieldSelect(true);
				}, 0);
			}
		}
	};

	const getSimulationDsplayName = (sim: SimulationData) =>
		`${sim.name} - ${sim.runDate}`;

	const renderSimulationNames = () =>
		riskStore.simulationData.map(sim => {
			const name = getSimulationDsplayName(sim);
			return (
				<MenuItem key={sim.uploadId} value={sim.uploadId}>
					{name}
				</MenuItem>
			);
		});

	const renderTaskNames = () =>
		riskStore.taskData.map(task => {
			return (
				<MenuItem key={task.uploadId} value={task.uploadId}>
					{task.name}
				</MenuItem>
			);
		});

	const onSimulationChange = (event: SelectChangeEvent) => {
		const uploadId = event?.target.value;
		const simulation = riskStore.simulationData.find(
			sim => sim.uploadId === uploadId,
		);
		if (simulation) {
			const simName = getSimulationDsplayName(simulation);
			setSimulationName(simName);
			setSimulationUploadId(simulation.uploadId);

			setSelectedCachedField(null);
			setField('');
			setFieldAlias('');
			setComponentChanged(true);
			if (onChange) onChange(true, false, false);
		}
	};

	const onSelectedTaskNameChange = (event: SelectChangeEvent) => {
		const uploadId = event?.target.value;
		const task = riskStore.taskData.find(_ => _.uploadId === uploadId);
		if (task) {
			setTaskName(task.name);
			setUploadId(task.uploadId);

			setSelectedCachedField(null);
			setField('');
			setFieldAlias('');
			setComponentChanged(true);
			if (onChange) onChange(true, false, false);
		}
	};

	const handleFieldSelectChangeSpatialAttr = (
		event: SelectChangeEvent<string>,
	) => {
		if (assetStore) {
			const target = event?.target as HTMLInputElement;
			const result = assetStore.spatialProximityAttr.find(
				({ cachedField }) => cachedField === target.value,
			);
			if (result) {
				setField(result.cachedField);
				setFieldAlias(result.userFriendlyName);
				setDistance(0);
				setComponentChanged(true);
				if (onChange) onChange(true, false, false);

				// This is a workaround to fix the MUI issue that disabling a select with focus does not correctly keep the focus when un-disabled
				setTimeout(() => {
					const field = document.getElementById('fieldSelectSA');
					if (field) field.blur();
					setDisableFieldSelect(true);
				}, 0);
			}
		}
	};

	const handleFieldSelectChangeSimulationAttr = (
		event: SelectChangeEvent<string>,
	) => {
		const target = event?.target as HTMLInputElement;
		const result = simulationCachedFields.find(
			({ userFriendlyName }) => userFriendlyName === target.value,
		);
		if (result) {
			setSelectedCachedField(result);
			setField(result.userFriendlyName);
			setFieldAlias(result.userFriendlyName);
			setComponentChanged(true);
			if (onChange) onChange(true, false, false);
		}
	};

	const handleTaskCachedFieldChange = (event: SelectChangeEvent<string>) => {
		const value = event.target.value;

		const result = taskCachedFields.find(
			({ cachedField }) => cachedField === value,
		);

		if (result) {
			setSelectedCachedField(result);
			setField(result.cachedField);
			setFieldAlias(result.userFriendlyName);
			setComponentChanged(true);
			if (onChange) onChange(true, false, false);
		}
	};

	const handleFieldSelectChangeLoF = (event: SelectChangeEvent<string>) => {
		if (lofStore) {
			const target = event?.target as HTMLInputElement;
			const result = (
				inspectionType
					? lofStore.componentInspectionFields
					: lofStore.componentFields
			).find(({ cachedField }) => cachedField === target.value);
			if (result) {
				setField(result.cachedField);
				setFieldAlias(result.userFriendlyName);
				setComponentChanged(true);
				if (onChange) onChange(true, false, false);

				// This is a workaround to fix the MUI issue that disabling a select with focus does not correctly keep the focus when un-disabled
				setTimeout(() => {
					const field = document.getElementById('fieldSelectA');
					if (field) field.blur();
					setDisableFieldSelect(true);
				}, 0);
			}
		}
	};

	const handleFieldSelectChangeCoF = (event: SelectChangeEvent<string>) => {
		if (cofStore) {
			const target = event?.target as HTMLInputElement;
			const result = cofStore.componentFields.find(
				({ cachedField }) => cachedField === target.value,
			);
			if (result) {
				setField(result.cachedField);
				setFieldAlias(result.userFriendlyName);
				setComponentChanged(true);
				if (onChange) onChange(true, false, false);

				// This is a workaround to fix the MUI issue that disabling a select with focus does not correctly keep the focus when un-disabled
				setTimeout(() => {
					const field = document.getElementById('fieldSelectA');
					if (field) field.blur();
					setDisableFieldSelect(true);
				}, 0);
			}
		}
	};

	const handleDistanceChange = (
		event: ChangeEvent<
			HTMLInputElementExtended | HTMLTextAreaElementExtended
		>,
	) => {
		const distance = parseFloat(event.target.value);
		setDistance(distance);
		setComponentChanged(true);
		if (onChange) onChange(true, false, false);
	};

	const validateName = (event: ChangeEvent<HTMLInputElement>) => {
		const name = event.target.value.toUpperCase();
		let error = '';
		if (name !== component.name.toUpperCase()) {
			if (name.trim().length == 0) {
				error = t('Enter a valid name');
			} else {
				let valid = true;
				if (parameterNumber === undefined) {
					if (forLoF) {
						valid =
							lofStore?.categories.find(cat =>
								cat.name === category?.name
									? cat.components.find(
											component =>
												component.name.toUpperCase() ==
												name,
									  )
									: undefined,
							) === undefined;
					} else {
						valid =
							cofStore?.categories.find(cat =>
								cat.name === category?.name
									? cat.components.find(
											catComponent =>
												catComponent.name.toUpperCase() ==
												name,
									  )
									: undefined,
							) === undefined;
					}
				} else if (parentComponent && parentComponent.paramComponents) {
					valid =
						parentComponent.paramComponents.find(
							(component, index) =>
								index !== parameterNumber &&
								component.name.toUpperCase() == name,
						) === undefined;
				}

				if (!valid) error = t('Enter a unique name');
			}
		}

		setValidName(error);
		setComponentName(error.length == 0 ? event.target.value : '');
		setComponentChanged(true);
		if (onChange) onChange(true, false, false);
	};

	const recalculateCount = (
		scoreRows: ScoreDataGridRow[],
		isUnique: boolean,
		changedRange: boolean,
		metadataType: string,
	) => {
		if (scoreRows && !isRiskCountLoading) {
			// let requestMetadata = component.metadata ?? [];
			let requestMetadata = metadata ?? [];

			if (scoreRows && scoreRows.length !== 0) {
				if (isUnique) {
					requestMetadata = scoreRows.map(row => ({
						key: row.value,
						value: row.score,
						ix: row.id,
					}));
				} else {
					requestMetadata = scoreRows
						.filter(row => row.value !== '--')
						.map(row => ({
							key: row.value,
							value: row.score,
							ix: row.id,
						}));
				}
			}

			setRequestedCountMetadata(requestMetadata);

			// Use the name to check if the configuration has been saved and if so then we can use the changedRangesKey if changed
			const key =
				component.name.length !== 0 && changedRange
					? changedRangesKey
					: countKey;
			if (isWebSocketEnabled) {
				dispatch(
					getRiskCountResolved({
						key: key,
						values: undefined,
					}),
				);
				dispatch(
					setRiskCountIsLoading({
						key: key,
						loading: true,
					}),
				);
			}
			switch (type) {
				case ComponentType.SpatialProximity:
					if (isWebSocketEnabled) {
						webSocket.sendMessage(
							JSON.stringify({
								path: 'risk',
								action: 'riskCalcCount',
								key: key,
								metadata: requestMetadata,
								metadataType: metadataType ?? 'unique',
								assetType: assetType,
								layerName: dataSource,
								fieldType: 'proximity',
							}),
						);
					} else {
						dispatch(
							getRiskCountProximity({
								key: key,
								metadata: requestMetadata,
								metadataType: metadataType ?? 'unique',
								assetType: assetType,
								layerName: dataSource,
							}),
						);
					}
					break;
				case ComponentType.SpatialAttribute:
					if (isWebSocketEnabled) {
						webSocket.sendMessage(
							JSON.stringify({
								path: 'risk',
								action: 'riskCalcCount',
								key: key,
								metadata: requestMetadata,
								metadataType: metadataType ?? 'unique',
								assetType: assetType,
								layerName: dataSource,
								fieldName: field ?? '',
								fieldType: 'proximityAttribute',
								valueType: '', // isUnique ? 'string' : 'number', // TODO - Need to work out how to get this/what it actually is
								distance: distance,
							}),
						);
					} else {
						dispatch(
							getRiskCountProximityAttribute({
								key: key,
								metadata: requestMetadata,
								metadataType: metadataType ?? 'unique',
								assetType: assetType,
								layerName: dataSource,
								fieldName: field ?? '',
								valueType: '', // isUnique ? 'string' : 'number', // TODO - Need to work out how to get this/what it actually is
								distance: distance,
							}),
						);
					}
					break;
				case ComponentType.CustomTable:
					if (isWebSocketEnabled) {
						webSocket.sendMessage(
							JSON.stringify({
								path: 'risk',
								action: 'riskCalcCount',
								key: key,
								metadata: requestMetadata,
								metadataType: metadataType ?? 'unique',
								assetType: assetType,
								fieldName: field ?? '',
								fieldType: 'customTable',
								tableName: dataSource,
								valueType: '', // TODO - NOT NEEDED??
							}),
						);
					} else {
						dispatch(
							getRiskCountCustomTable({
								key: key,
								metadata: requestMetadata,
								metadataType: metadataType ?? 'unique',
								assetType: assetType,
								fieldName: field ?? '',
								tableName: dataSource,
								valueType: '', // TODO - NOT NEEDED??
							}),
						);
					}
					break;
				case ComponentType.Task:
					if (isWebSocketEnabled) {
						if (isRiskCountTaskEnabled) {
							webSocket.sendMessage(
								JSON.stringify({
									path: 'risk',
									action: 'riskCalcCount',
									key: key,
									metadata: requestMetadata,
									metadataType: metadataType ?? 'unique',
									assetType: assetType,
									fieldName: field ?? '',
									fieldType: 'task',
									valueType: '', // TODO - NOT NEEDED??
									uploadId: uploadId,
								}),
							);
						}
					} else {
						if (isRiskCountTaskEnabled) {
							dispatch(
								getRiskCountTask({
									key: key,
									metadata: requestMetadata,
									metadataType: metadataType ?? 'unique',
									assetType: assetType,
									fieldName: field ?? '',
									tableName: getTaskLayerName(
										taskName,
										uploadId,
									),
									valueType: '', // TODO - NOT NEEDED??
									inspectionType: uploadId,
								}),
							);
						}
					}
					break;
				case ComponentType.Simulation:
				case ComponentType.Observation:
					// Dont do counts for these
					dispatch(
						setRiskCountIsLoading({
							key: key,
							loading: false,
						}),
					);
					break;
				default:
					{
						if (inspectionType) {
							if (isWebSocketEnabled) {
								webSocket.sendMessage(
									JSON.stringify({
										path: 'risk',
										action: 'riskCalcCount',
										key: key,
										metadata: requestMetadata,
										metadataType: metadataType ?? 'unique',
										assetType: assetType,
										fieldName: field ?? '',
										fieldType: 'inspection',
										inspectionType: inspectionType,
									}),
								);
							} else {
								dispatch(
									getRiskCountInspection({
										key: key,
										metadata: requestMetadata,
										metadataType: metadataType ?? 'unique',
										assetType: assetType,
										fieldName: field ?? '',
										inspectionType: inspectionType,
									}),
								);
							}
						} else {
							if (isWebSocketEnabled) {
								webSocket.sendMessage(
									JSON.stringify({
										path: 'risk',
										action: 'riskCalcCount',
										key: key,
										metadata: requestMetadata,
										metadataType: metadataType ?? 'unique',
										assetType: assetType,
										fieldName: field ?? '',
										fieldType: 'attribute',
									}),
								);
							} else {
								dispatch(
									getRiskCountAttribute({
										key: key,
										metadata: requestMetadata,
										metadataType: metadataType ?? 'unique',
										assetType: assetType,
										fieldName: field ?? '',
									}),
								);
							}
						}
					}
					break;
			}
		}
	};

	const onRecalculateCount = () => {
		recalculateCount(scoreRows, isUnique, rangeChanged, metadataType);
	};

	const onRecalculateCountMatrix = () => {
		if (
			component.scoreMatrix &&
			componentParameter1 &&
			componentParameter1.metadata &&
			componentParameter2 &&
			componentParameter2.metadata
		) {
			const scoreComponents: ComponentCount[] = [
				getComponentCount(componentParameter1),
				getComponentCount(componentParameter2),
			];
			// Update the matrix with the current score
			if (multiScoreEdit.length !== 0) {
				const matrix: MultiScoreItem[] = [];
				let index = 0;
				for (let i = 0; i < componentParameter2.metadata.length; i++) {
					const row: MultiScoreItem = {
						id: i,
						param2: componentParameter2.metadata[i].key,
					};

					for (
						let i2 = 0;
						i2 < componentParameter1.metadata.length;
						i2++
					) {
						const score =
							(multiScoreEdit[i][`${i2}-score`] as number) ?? 0;
						component.scoreMatrix[index].score = score;
						row[`${i2}-score`] = score;
						row[`${i2}-count`] = 0;
						index++;
					}

					matrix.push(row);
				}

				setComponentMatrix(matrix);
			}

			// Get the counts for the matrix
			const key = 'ScoreMatrix';

			if (isWebSocketEnabled) {
				dispatch(
					setRiskCountIsLoading({
						key: key,
						loading: true,
					}),
				);
				webSocket.sendMessage(
					JSON.stringify({
						path: 'risk',
						action: 'riskCalcCount',
						key: key,
						fieldType: 'multiParam',
						scoreMatrix: component.scoreMatrix,
						assetType: assetType,
						inspectionType: inspectionType,
						paramComponents: scoreComponents,
					}),
				);
			} else {
				dispatch(
					getRiskCountScoreMatrix({
						key: key,
						scoreMatrix: component.scoreMatrix,
						assetType: assetType,
						inspectionType: inspectionType,
						components: scoreComponents,
					}),
				);
			}
		}
	};

	const createScoreRowsFromComponent = (
		changeComponent: boolean,
		changedRange: boolean,
		metadataType: string,
		nullScore: number,
		metadata?: ComponentMetadata[],
		unitMetadata?: UnitMetadata,
	) => {
		if (metadata) {
			let newScoreRows: ScoreDataGridRow[] = [];
			let isUnique = false;
			let fetchCounts =
				riskCounts === undefined ||
				riskCounts.length === 0 ||
				changeComponent ||
				component.name.length === 0;

			if (metadataType === 'unique') {
				isUnique = true;

				const sorted = [...metadata];
				if (inspectionType === undefined) {
					// Sort forcing the '--' (null entry) to the bottom
					sorted.sort((a, b) => {
						if (a.key === '--') return 1;
						if (b.key === '--') return -1;
						return a.key.toString().localeCompare(b.key.toString());
					});
				}

				newScoreRows = sorted.map((row, index) => ({
					id: index,
					score: row.value,
					value: row.key,
				}));

				if (riskCounts && !fetchCounts) {
					// We cant verify spatial attribute data as the scores could not match the request due to items not being in range
					// Or if we have missmatched lenghts, again because the server does not return some empty rows
					if (
						type !== ComponentType.SpatialAttribute &&
						riskCounts.length === newScoreRows.length
					) {
						// But everything else  they actually match just in case
						for (let i = 0; i < newScoreRows.length - 1; i++) {
							if (
								!riskCounts.find(
									({ key }) =>
										(key === undefined || key === null
											? '--'
											: key.toString()) ===
										newScoreRows[i].value.toString(),
								)
							) {
								fetchCounts = true;
								break;
							}
						}
					}

					if (!fetchCounts) {
						newScoreRows = updateRiskCounts(
							newScoreRows,
							riskCounts,
							system == 'Metric',
							true,
						);
					}
				}

				dispatch(setRiskScoreDataGrid(newScoreRows));
				dispatch(setRiskUnitScores(undefined));
				dispatch(setRiskEditScores([]));
				dispatch(setRiskScores([]));
			} else if (metadata.length !== 0) {
				newScoreRows = metadata.map((row, index) => ({
					id: index,
					score: row.value,
					value: row.key,
				}));

				if (
					inspectionType === undefined &&
					newScoreRows[newScoreRows.length - 1].value !== '--'
				) {
					// Add the null score
					newScoreRows.push({
						id: newScoreRows.length,
						score: nullScore ?? 0,
						value: '--',
					});
				}

				if (riskCounts && !fetchCounts) {
					// Veify they actually match just in case
					for (let i = 0; i < newScoreRows.length - 1; i++) {
						if (
							!riskCounts.find(
								({ key }) =>
									(key === undefined || key === null
										? '--'
										: key.toString()) ===
									newScoreRows[i].value,
							)
						) {
							fetchCounts = true;
							break;
						}
					}

					if (!fetchCounts) {
						newScoreRows = updateRiskCounts(
							newScoreRows,
							riskCounts,
							system == 'Metric',
							true,
						);
					}
				}

				dispatch(setRiskScoreDataGrid(newScoreRows));

				// Extract the scores, ignoring the null score if needed
				const offset = inspectionType === undefined ? 1 : 0;
				let scores: number[] = [newScoreRows.length - offset];
				for (let i = 0; i < newScoreRows.length - offset; i++) {
					const values = newScoreRows[i].value.split(' ');
					if (values.length == 2) scores[i] = parseFloat(values[1]);
					else scores[i] = parseFloat(values[2]);
				}

				// If we have a unit we may need to do some covnersion stuff later
				if (
					system !== 'Metric' &&
					unitMetadata &&
					unitMetadata.unit &&
					unitMetadata.unit.length !== 0
				) {
					const precision = unitMetadata.precision ?? 0;
					// Create an array with the values in the system display value what ever that might be
					const unitScores = scores.map(score => {
						return parseFloat(
							parseFloat(
								getSystemValueNoIntlFormat(
									`${score} ${unitMetadata.unit}`,
								),
							).toFixed(precision),
						);
					});
					// Now convert the rounded conversion back to metric.... I know !!!
					const internalUnit = getSystemUnit(unitMetadata.unit);
					let adjustment = 1;
					// mm -> in -> cm
					if (unitMetadata.unit === 'mm') adjustment = 10;

					scores = unitScores.map(score => {
						return parseFloat(
							(
								parseFloat(
									getStandardisedValue(
										`${score} ${internalUnit}`,
									),
								) * adjustment
							).toFixed(precision),
						);
					});
					dispatch(setRiskUnitScores(unitScores));
				} else {
					dispatch(setRiskUnitScores(undefined));
				}

				const stringScores = scores.map(score => {
					return score?.toString();
				});
				dispatch(setRiskEditScores(stringScores));
				dispatch(setRiskScores(scores));
			}

			// Fetch the counts and lengths
			if (fetchCounts) {
				recalculateCount(
					newScoreRows,
					isUnique,
					changedRange,
					metadataType,
				);
			}
			setIsUnique(isUnique);
			setShowScoreTable(true);
		}
	};

	const updateComponent = () => {
		if (isNew || parameterNumber !== undefined) {
			component.type = type;
			component.field = field;
			component.fieldAlias = fieldAlias;
			component.simulationName = simulationName;
			component.taskName = taskName;
			component.uploadId = uploadId;
			component.simulationUploadId = simulationUploadId;
			component.layer =
				type === ComponentType.Task
					? getTaskLayerName(taskName, uploadId)
					: dataSource;
		}

		component.maxDistance = distance;

		if (isUnique) {
			component.metadata = scoreRows.map(row => ({
				key: row.value,
				value: row.score,
				ix: row.id,
			}));
		} else {
			if (parameterNumber === undefined) {
				component.metadata = scoreRows
					.filter(row => row.value !== '--')
					.map(row => ({
						key: row.value,
						value: row.score,
						ix: row.id,
					}));
			} else {
				// Dont filter out the '--' for multi param
				component.metadata = scoreRows.map(row => ({
					key: row.value,
					value: row.score,
					ix: row.id,
				}));
			}
		}
		component.metadataType = metadataType;

		if (
			type === ComponentType.MultiParam &&
			componentParameter1 &&
			componentParameter2
		) {
			component.paramComponents = [
				componentParameter1,
				componentParameter2,
			];
		}

		// If we are saving components for the first time then move to the next tab
		const changeTab = component.name === '';

		if (component.name != componentName) {
			if (parameterNumber === undefined) {
				dispatch(
					renameRiskCount({
						existingKey: countKey,
						newKey: countKey.replace(
							`-${component.name}`,
							`-${componentName}`,
						),
					}),
				);
			}
			component.name = componentName;
		}

		component.unitMetadata = unitMetadata;
		component.unitMetadataName = unitMetadataName;

		const nullScore = scoreRows.find(row => row.value === '--');
		component.nullScore = nullScore ? nullScore.score : 0;

		dispatch(
			renameRiskCount({
				existingKey: changedRangesKey,
				newKey: countKey,
			}),
		);

		setComponentChanged(false);
		if (onChange) onChange(false, true, changeTab);
	};

	useEffect(() => {
		if (type === ComponentType.MultiParam) {
			setShowScoreTable(false);
			setShowMutliParam(true);
		} else {
			let valid = false;

			switch (type) {
				case ComponentType.Attribute:
					valid = field.length !== 0;
					break;
				case ComponentType.SpatialProximity:
					valid = dataSource.length !== 0;
					break;
				case ComponentType.SpatialAttribute:
					valid = field.length !== 0 && dataSource.length !== 0;
					break;
				case ComponentType.CustomTable:
					valid = field.length !== 0 && dataSource.length !== 0;
					break;
				case ComponentType.Simulation:
				case ComponentType.Task:
					valid = field.length !== 0;
					break;
				case ComponentType.Observation:
					valid = field.length !== 0 && defects.length !== 0;
					break;
			}

			setValidConfig(valid);
			setShowScoreTable(false);
			setShowMutliParam(false);

			if ((isNew || parameterNumber !== undefined) && valid) {
				if (
					component.unitMetadataName !==
					`${field}-${type}-${dataSource}`
				) {
					component.unitMetadata = undefined;
					component.unitMetadataName = undefined;
					setRangeClasses(5);
					setHasMap(
						type === ComponentType.SpatialProximity ||
							type === ComponentType.SpatialAttribute,
					);
					fetchMetadata(false, parameterNumber !== undefined);
				}
			}
		}
	}, [field, type, dataSource, defects]);

	const fetchMetadata = (disableField: boolean, multiParam: boolean) => {
		const metaDataName =
			type === ComponentType.Task
				? `${field}-${type}-${uploadId}`
				: `${field}-${type}-${dataSource}`;

		if ((field || dataSource) && unitMetadataName !== metaDataName) {
			if (disableField) setDisableFieldSelect(true);

			switch (type) {
				case ComponentType.SpatialAttribute: {
					setFetchingMetadata(true);
					if (forLoF) {
						dispatch(
							getLoFComponentProximityAttrMetadata({
								assetType: assetType,
								field: field ?? '',
								fieldAlias: field ?? '',
								layerName: dataSource,
								multiParam,
							}),
						);
					} else {
						dispatch(
							getCoFComponentProximityAttrMetadata({
								assetType: assetType,
								field: field ?? '',
								fieldAlias: field ?? '',
								layerName: dataSource,
								multiParam,
							}),
						);
					}
					break;
				}
				case ComponentType.SpatialProximity: {
					setFetchingMetadata(true);
					if (forLoF) {
						dispatch(
							getLoFComponentAssetMetadata({
								assetType: assetType,
								field: 'proximity',
								fieldAlias: dataSource ?? '',
								multiParam,
							}),
						);
					} else {
						dispatch(
							getCoFComponentAssetMetadata({
								assetType: assetType,
								field: 'proximity',
								fieldAlias: dataSource ?? '',
								multiParam,
							}),
						);
					}
					break;
				}
				case ComponentType.CustomTable: {
					setFetchingMetadata(true);
					if (forLoF) {
						dispatch(
							getLoFComponentCustomTableMetadata({
								assetType: assetType,
								field: field ?? '',
								fieldAlias: fieldAlias ?? '',
								layerName: dataSource,
								multiParam,
							}),
						);
					} else {
						dispatch(
							getCoFComponentCustomTableMetadata({
								assetType: assetType,
								field: field ?? '',
								fieldAlias: fieldAlias ?? '',
								layerName: dataSource,
								multiParam,
							}),
						);
					}
					break;
				}
				case ComponentType.Task:
					setFetchingMetadata(true);
					if (forLoF) {
						dispatch(
							getLoFComponentTaskMetadata({
								assetType: assetType,
								field: field ?? '',
								fieldAlias: fieldAlias ?? '',
								layerName: getTaskLayerName(taskName, uploadId),
								multiParam,
							}),
						);
					} else {
						dispatch(
							getCoFComponentTaskMetadata({
								assetType: assetType,
								field: field ?? '',
								fieldAlias: fieldAlias ?? '',
								layerName: getTaskLayerName(taskName, uploadId),
								multiParam,
							}),
						);
					}
					break;
				case ComponentType.Observation:
				case ComponentType.Simulation: {
					// Not needed for Simulation or Observation
					break;
				}
				default: {
					setFetchingMetadata(true);
					if (forLoF) {
						if (inspectionType) {
							dispatch(
								getLoFComponentInspectionMetadata({
									assetType: assetType,
									field: field ?? '',
									fieldAlias: fieldAlias ?? '',
									multiParam,
								}),
							);
						} else {
							dispatch(
								getLoFComponentAssetMetadata({
									assetType: assetType,
									field: field ?? '',
									fieldAlias: fieldAlias ?? '',
									multiParam,
								}),
							);
						}
					} else {
						dispatch(
							getCoFComponentAssetMetadata({
								assetType: assetType,
								field: field ?? '',
								fieldAlias: fieldAlias ?? '',
								multiParam,
							}),
						);
					}
					break;
				}
			}
		}
	};

	const createDefaultRangeValues = (
		newRangeClasses: number,
		unitMetadata?: UnitMetadata,
	): ComponentMetadata[] | undefined => {
		let metadata = undefined;
		if (unitMetadata) {
			if (unitMetadata.range) {
				metadata = new Array(newRangeClasses + 2);
				const defaultScore: number[] = [metadata.length];

				let rangeMin = unitMetadata.range.min;
				if (rangeMin == null || rangeMin == undefined) {
					rangeMin = 0;
				}

				let rangeMax = unitMetadata.range.max;
				if (rangeMax == null || rangeMax == undefined) {
					rangeMax = 0;
				}

				// Do we have to care about units at this point?
				if (unitMetadata.unit && system !== 'Metric') {
					const precision: number = unitMetadata.precision ?? 0;
					// To make the ranges work we need to do a set in native unit and one in display units
					// Otherwise the initial display can have too many decimal places until the user edits a value
					const internalUnit = getSystemUnit(unitMetadata.unit);
					const adjustment = unitMetadata.unit === 'mm' ? 10 : 1;
					const unitMin = parseFloat(
						getSystemValueNoIntlFormat(
							`${rangeMin} ${unitMetadata.unit}`,
						),
					);
					const unitMax = parseFloat(
						getSystemValueNoIntlFormat(
							`${rangeMax} ${unitMetadata.unit}`,
						),
					);

					const unitRange = unitMax - unitMin;

					let interval = unitRange / newRangeClasses;
					// If the interval is 0 then make it the minimum increment
					if (interval === 0) {
						interval = +(1 / Math.pow(10, precision)).toFixed(
							precision,
						);
					} else {
						interval = +interval.toFixed(precision);
					}

					let first = unitMin;
					let nativeFirst = rangeMin;
					let pos = 0;

					defaultScore[pos] = nativeFirst;
					metadata[pos] = {
						key: ` <= ${nativeFirst}`,
						value: 0,
						ix: pos++,
					};

					for (let i = 0; i < newRangeClasses - 1; i++) {
						const next = +(first + interval).toFixed(precision);
						const nativeNext =
							parseFloat(
								getStandardisedValue(`${next} ${internalUnit}`),
							) * adjustment;

						metadata[pos] = {
							key: `${nativeFirst} <= ${nativeNext}`,
							value: 0,
							ix: pos++,
						};
						defaultScore[pos] = nativeNext;
						nativeFirst = nativeNext;
						first = next;
					}

					// Ignore the range max as it could be less than we need to make a unique range
					const max = first + interval;
					const nativeMax =
						parseFloat(
							getStandardisedValue(`${max} ${internalUnit}`),
						) * adjustment;

					defaultScore[pos] = nativeMax;
					metadata[pos] = {
						key: `${nativeFirst} <= ${nativeMax}`,
						value: 0,
						ix: pos++,
					};

					defaultScore[pos] = nativeMax;
					metadata[pos] = {
						key: `> ${nativeMax}`,
						value: 0,
						ix: pos++,
					};
				} else {
					const precision: number = unitMetadata.precision ?? 0;
					const range = rangeMax - rangeMin;
					let interval = range / newRangeClasses;
					// If the interval is 0 then make it the minimum increment
					if (interval === 0) {
						interval = +(1 / Math.pow(10, precision)).toFixed(
							precision,
						);
					} else {
						interval = +interval.toFixed(precision);
					}

					let first = rangeMin;
					let pos = 0;

					defaultScore[pos] = first;
					metadata[pos] = {
						key: ` <= ${first}`,
						value: 0,
						ix: pos++,
					};

					for (let i = 0; i < newRangeClasses - 1; i++) {
						const next = +(first + interval).toFixed(precision);
						metadata[pos] = {
							key: `${first} <= ${next}`,
							value: 0,
							ix: pos++,
						};
						defaultScore[pos] = next;
						first = next;
					}

					// Ignore the range max as it could be less than we need to make a unique range
					const max = first + interval;
					defaultScore[pos] = max;
					metadata[pos] = {
						key: `${first} <= ${max}`,
						value: 0,
						ix: pos++,
					};

					defaultScore[pos] = max;
					metadata[pos] = {
						key: `> ${max}`,
						value: 0,
						ix: pos++,
					};
				}

				setMetadata(metadata);
				setMetadataType('range');
				setNullScore(0);

				const stringScores = defaultScore.map(score => {
					return score?.toString();
				});
				dispatch(setRiskEditScores(stringScores));
				dispatch(setRiskScores(defaultScore));
				setRangeClasses(newRangeClasses);
			}
		}
		return metadata;
	};

	const typeInputs = () => {
		if (isNew) {
			return (
				<FormControl variant="outlined" margin="normal" fullWidth>
					<InputLabel>{t('Type')}</InputLabel>
					<Select
						data-cy="typeField"
						name="type"
						onChange={handleTypeSelectChange}
						disabled={isRiskCountLoading || fetchingMetadata}
						defaultValue={type}
						label={t('Type')}>
						{menuItemsType}
					</Select>
				</FormControl>
			);
		} else {
			return (
				<FormControl variant="outlined" margin="normal" fullWidth>
					<TextField
						fullWidth
						data-cy="typeField"
						name="type"
						label={t('Type')}
						variant="outlined"
						defaultValue={getTypeDisplay(type, t)}
						disabled={true}
					/>
				</FormControl>
			);
		}
	};

	const attrInputs = () => {
		if (isNew) {
			return (
				<FormControl variant="outlined" margin="normal" fullWidth>
					<InputLabel>{t('Field')}</InputLabel>
					<Select
						id={'fieldSelectA'}
						key={`f${dataSource}${Date.now}`}
						data-cy="fieldField"
						name="field"
						defaultValue={field}
						onChange={
							forLoF
								? handleFieldSelectChangeLoF
								: handleFieldSelectChangeCoF
						}
						disabled={isRiskCountLoading || disableFieldSelect}
						label={t('Field')}>
						{forLoF
							? inspectionType
								? menuItemsInspectionLoF
								: menuItemsAssetLoF
							: menuItemsCoF}
					</Select>
				</FormControl>
			);
		} else {
			return (
				<FormControl variant="outlined" margin="normal" fullWidth>
					<TextField
						fullWidth
						data-cy="fieldField"
						name="field"
						label={t('Field')}
						variant="outlined"
						defaultValue={fieldAlias}
						disabled={true}
					/>
				</FormControl>
			);
		}
	};

	const handleObservationFieldInputChange = (
		_event: React.SyntheticEvent,
		values: unknown[],
	) => {
		setDefects(values as string[]);
		setComponentChanged(true);
		if (onChange) onChange(true, false, false);
	};

	const dataSourceSelect = useMemo(() => {
		switch (type) {
			case ComponentType.SpatialProximity:
				return menuItemsLayerList;
			case ComponentType.SpatialAttribute:
				return menuItemsSpatialAttrList;
			case ComponentType.CustomTable:
				return menuItemsCustomTableList;
		}
	}, [type]);

	const dataSourceInputs = () => {
		if (isNew) {
			return (
				<FormControl
					variant="outlined"
					margin="normal"
					fullWidth
					disabled={isRiskCountLoading || fetchingMetadata}>
					<InputLabel>{t('Data Source')}</InputLabel>
					<Select
						key={`ds${type}${Date.now}`}
						data-cy="dataSourceField"
						name="dataSource"
						defaultValue={dataSource}
						onChange={handleDataSourceSelectChange}
						label={t('Data Source')}>
						{dataSourceSelect}
					</Select>
				</FormControl>
			);
		} else {
			return (
				<FormControl variant="outlined" margin="normal" fullWidth>
					<TextField
						fullWidth
						data-cy="dataSourceField"
						name="dataSource"
						label={t('Data Source')}
						variant="outlined"
						defaultValue={dataSource}
						disabled={true}
					/>
				</FormControl>
			);
		}
	};

	const spatialAttriFieldInputs = () => {
		if (isNew) {
			return (
				<FormControl variant="outlined" margin="normal" fullWidth>
					<InputLabel>{t('Field')}</InputLabel>
					<Select
						id={'fieldSelectSA'}
						key={`f${dataSource}${Date.now}`}
						data-cy="fieldField"
						name="saField"
						defaultValue={field}
						onChange={handleFieldSelectChangeSpatialAttr}
						disabled={isRiskCountLoading || disableFieldSelect}
						label={t('Field')}>
						{menuItemsSpatialAttrFieldList}
					</Select>
				</FormControl>
			);
		} else {
			return (
				<FormControl variant="outlined" margin="normal" fullWidth>
					<TextField
						fullWidth
						data-cy="fieldField"
						name="safield"
						label={t('Field')}
						variant="outlined"
						defaultValue={field}
						disabled={true}
					/>
				</FormControl>
			);
		}
	};

	const distanceInputs = () => {
		// Distance is always editable
		return (
			<FormControl variant="outlined" margin="normal" fullWidth>
				<UnitField
					key={`dis${field}`}
					fullWidth
					data-cy="distanceField"
					name="distance"
					unit="m"
					step="1"
					min={0}
					defaultValue={distance.toString()}
					label={t('Distance')}
					variant="outlined"
					onChange={handleDistanceChange}
					disabled={
						isRiskCountLoading ||
						fetchingMetadata ||
						disableFieldSelect
					}
				/>
			</FormControl>
		);
	};

	const customTableInputs = () => {
		if (isNew) {
			return (
				<FormControl variant="outlined" margin="normal" fullWidth>
					<InputLabel>{t('Field')}</InputLabel>
					<Select
						id={'fieldSelectCT'}
						key={`f${dataSource}${Date.now}`}
						data-cy="fieldField"
						name="ctfield"
						defaultValue={field}
						onChange={handleFieldSelectChangeCustomTable}
						disabled={isRiskCountLoading || disableFieldSelect}
						label={t('Field')}>
						{menuItemsCustomTableFieldList}
					</Select>
				</FormControl>
			);
		} else {
			return (
				<FormControl variant="outlined" margin="normal" fullWidth>
					<TextField
						fullWidth
						data-cy="fieldField"
						name="ctfield"
						label={t('Field')}
						variant="outlined"
						defaultValue={fieldAlias}
						disabled={true}
					/>
				</FormControl>
			);
		}
	};

	const simulationCachedFields = useMemo(() => {
		return (
			riskStore.simulationData.find(
				sim => sim.uploadId === simulationUploadId,
			)?.cachedFields ?? []
		);
	}, [simulationUploadId]);

	const menuItemsSimulationFieldList = useMemo(() => {
		return simulationCachedFields.map((field, index) => {
			return (
				<MenuItem
					key={field.cachedField + index}
					value={field.userFriendlyName}>
					{field.userFriendlyName}
				</MenuItem>
			);
		});
	}, [simulationCachedFields]);

	const taskCachedFields = useMemo(() => {
		return (
			riskStore.taskData.find(_ => _.uploadId === uploadId)
				?.cachedFields ?? []
		);
	}, [uploadId]);

	const taskCachedFieldListItems = useMemo(() => {
		return taskCachedFields.map(field => {
			return (
				<MenuItem key={field.cachedField} value={field.cachedField}>
					{fixupTaskField(t, field.userFriendlyName)}
				</MenuItem>
			);
		});
	}, [taskCachedFields]);

	const renderThirdFormControl = ({
		title,
		onChange,
		renderValues,
		defaultValue,
		valueWhenDisabled,
	}: RenderThirdFormControlProps) => {
		if (isNew) {
			return (
				<FormControl variant="outlined" margin="normal" fullWidth>
					<InputLabel>{title}</InputLabel>
					<Select
						id={`fieldSelect${title}`}
						key={`f${dataSource}${Date.now}`}
						data-cy={`thirdFormControl${title}`}
						name={`thirdFormControl${title}`}
						onChange={onChange}
						defaultValue={defaultValue}
						disabled={isRiskCountLoading || disableFieldSelect}
						label={t(title)}>
						{renderValues()}
					</Select>
				</FormControl>
			);
		} else {
			return (
				<FormControl variant="outlined" margin="normal" fullWidth>
					<TextField
						fullWidth
						data-cy="fieldNameField"
						name="fnfield"
						label={t(title)}
						variant="outlined"
						defaultValue={valueWhenDisabled}
						disabled={true}
					/>
				</FormControl>
			);
		}
	};

	const renderSimulationNameControl = () => {
		if (isNew) {
			return (
				<FormControl variant="outlined" margin="normal" fullWidth>
					<InputLabel>{t('Simulation')}</InputLabel>
					<Select
						id={'fieldSelectSimulation'}
						key={`f${dataSource}`}
						data-cy="simulationFieldField"
						name="simulationField"
						defaultValue={simulationUploadId}
						onChange={onSimulationChange}
						disabled={isRiskCountLoading || disableFieldSelect}
						label={t('Simulation')}>
						{renderSimulationNames()}
					</Select>
				</FormControl>
			);
		} else {
			return (
				<FormControl variant="outlined" margin="normal" fullWidth>
					<TextField
						fullWidth
						data-cy="fieldField"
						name="ctfield"
						label={t('Field')}
						variant="outlined"
						defaultValue={simulationName}
						disabled={true}
					/>
				</FormControl>
			);
		}
	};

	const rendersimulationCachedFieldControl = () => {
		if (isNew) {
			return (
				<FormControl variant="outlined" margin="normal" fullWidth>
					<InputLabel>{t('Field')}</InputLabel>
					<Select
						id={'fieldSelectSIM'}
						key={`f${simulationName}${Date.now}`}
						data-cy="fieldField"
						name="field"
						defaultValue={field}
						onChange={handleFieldSelectChangeSimulationAttr}
						disabled={isRiskCountLoading || disableFieldSelect}
						label={t('Field')}>
						{menuItemsSimulationFieldList}
					</Select>
				</FormControl>
			);
		} else {
			return (
				<FormControl variant="outlined" margin="normal" fullWidth>
					<TextField
						fullWidth
						data-cy="fieldField"
						name="field"
						label={t('Field')}
						variant="outlined"
						defaultValue={fieldAlias}
						disabled={true}
					/>
				</FormControl>
			);
		}
	};

	const renderTaskCachedFieldControl = () => {
		if (isNew) {
			return (
				<FormControl variant="outlined" margin="normal" fullWidth>
					<InputLabel>{t('Field')}</InputLabel>
					<Select
						id={'fieldSelectSIM'}
						key={`f${taskName}${Date.now}`}
						data-cy="fieldField"
						name="field"
						defaultValue={field}
						onChange={handleTaskCachedFieldChange}
						disabled={isRiskCountLoading || disableFieldSelect}
						label={t('Field')}>
						{taskCachedFieldListItems}
					</Select>
				</FormControl>
			);
		} else {
			return (
				<FormControl variant="outlined" margin="normal" fullWidth>
					<TextField
						fullWidth
						data-cy="fieldField"
						name="field"
						label={t('Field')}
						variant="outlined"
						defaultValue={fixupTaskField(t, fieldAlias)}
						disabled={true}
					/>
				</FormControl>
			);
		}
	};

	const componentSetup = () => {
		let setup = <Grid item xs={10}></Grid>;
		let nextButton;

		if (parameterNumber !== undefined) {
			nextButton = (
				<Grid
					item
					xs={2}
					paddingLeft={'20px'}
					display="flex"
					justifyContent="flex-end">
					<div>
						<Button
							style={{ marginTop: '16px' }}
							disabled={
								metadataStore?.isLoading ||
								isRiskCountLoading ||
								fetchingMetadata ||
								!showScoreTable ||
								validName.length !== 0 ||
								!componentChanged
							}
							variant="outlined"
							onClick={updateComponent}>
							{component.name === ''
								? t('Set Parameter')
								: t('Update Parameter')}
						</Button>
					</div>
				</Grid>
			);
		}

		switch (type) {
			case ComponentType.Attribute:
				setup = (
					<>
						<Grid item xs={2} paddingLeft={'20px'}>
							{typeInputs()}
						</Grid>
						<Grid item xs={2} paddingLeft={'20px'}>
							{attrInputs()}
						</Grid>
						<Grid item xs={4}></Grid>
						{nextButton}
					</>
				);
				break;
			case ComponentType.SpatialProximity:
				setup = (
					<>
						<Grid item xs={2} paddingLeft={'20px'}>
							{typeInputs()}
						</Grid>
						<Grid item xs={2} paddingLeft={'20px'}>
							{dataSourceInputs()}
						</Grid>
						<Grid item xs={4}></Grid>
						{nextButton}
					</>
				);
				break;
			case ComponentType.SpatialAttribute:
				setup = (
					<>
						<Grid item xs={2} paddingLeft={'20px'}>
							{typeInputs()}
						</Grid>
						<Grid item xs={2} paddingLeft={'20px'}>
							{dataSourceInputs()}
						</Grid>
						<Grid item xs={2} paddingLeft={'20px'}>
							{dataSource && spatialAttriFieldInputs()}
						</Grid>
						<Grid item xs={2} paddingLeft={'20px'}>
							{dataSource && distanceInputs()}
						</Grid>
						{nextButton}
					</>
				);
				break;
			case ComponentType.CustomTable:
				setup = (
					<>
						<Grid item xs={2} paddingLeft={'20px'}>
							{typeInputs()}
						</Grid>
						<Grid item xs={2} paddingLeft={'20px'}>
							{dataSourceInputs()}
						</Grid>
						<Grid item xs={2} paddingLeft={'20px'}>
							{dataSource && customTableInputs()}
						</Grid>
						<Grid item xs={2}></Grid>
						{nextButton}
					</>
				);
				break;
			case ComponentType.Observation:
				setup = (
					<>
						<Grid item xs={2} paddingLeft={'20px'}>
							{typeInputs()}
						</Grid>
						<Grid item xs={2} paddingLeft={'20px'}>
							{renderObservationFieldsForm()}
						</Grid>
						<Grid item xs={4} paddingLeft={'20px'}>
							{renderObservationCodeValueForm()}
						</Grid>
						{nextButton}
					</>
				);
				break;
			case ComponentType.MultiParam:
				setup = (
					<>
						<Grid item xs={2} paddingLeft={'20px'}>
							{typeInputs()}
						</Grid>
					</>
				);
				break;
			case ComponentType.Simulation:
				if (isRiskSimulationEnabled && isSimulationEnabled) {
					setup = (
						<>
							<Grid item xs={2} paddingLeft={'20px'}>
								{typeInputs()}
							</Grid>
							<Grid item xs={2} paddingLeft={'20px'}>
								{renderSimulationNameControl()}
							</Grid>
							<Grid item xs={2} paddingLeft={'20px'}>
								{simulationName &&
									rendersimulationCachedFieldControl()}
							</Grid>
							<Grid item xs={2}></Grid>
							{nextButton}
						</>
					);
				}
				break;
			case ComponentType.Task:
				setup = (
					<>
						<Grid item xs={2} paddingLeft={'20px'}>
							{typeInputs()}
						</Grid>
						<Grid item xs={2} paddingLeft={'20px'}>
							{renderThirdFormControl({
								title: t('Task Name'),
								onChange: onSelectedTaskNameChange,
								renderValues: renderTaskNames,
								defaultValue: uploadId,
								valueWhenDisabled: taskName,
							})}
						</Grid>
						<Grid item xs={2} paddingLeft={'20px'}>
							{taskName && renderTaskCachedFieldControl()}
						</Grid>
						<Grid item xs={2}></Grid>
						{nextButton}
					</>
				);
				break;
		}

		return setup;
	};

	useEffect(() => {
		if (isNew && component.name.length === 0) {
			// New component to clean up as required
			clearFields(false);
			dispatch(clearComponentMetadata());
			dispatch(setRiskScoreDataGrid([]));
			dispatch(setRiskUnitScores(undefined));
			dispatch(setRiskEditScores([]));
			dispatch(setRiskScores([]));
			setShowScoreTable(false);
		} else if (type == ComponentType.MultiParam) {
			// Load up the parameters
			if (component.paramComponents) {
				// Load the parameters
				setComponentParameter1({ ...component.paramComponents[0] });
				setComponentParameter2({ ...component.paramComponents[1] });
				// Now process the scoreMatrix
				updateComponentMatrix();
				setCurrentIndex(2);
			} else {
				//Something is not right.. tell the user and cancel out
				dispatch(error(t('Invalid Multi Parameter Component!')));
				onCancel();
			}
		} else if (field || dataSource || defects) {
			fetchMetadata(true, parameterNumber !== undefined);
			createScoreRowsFromComponent(
				component.name.length === 0,
				rangeChanged,
				metadataType,
				nullScore,
				metadata,
				unitMetadata,
			);
		}
	}, [component]);

	useEffect(() => {
		if (
			riskCounts &&
			riskCounts.length !== 0 &&
			// scoreRows can get updated AFTER the risk count is updated so we need to verify they actually match
			(metadata === undefined ||
				(scoreRows.length ==
					metadata.length +
						(inspectionType !== undefined ||
						metadataType === 'unique' ||
						metadata[metadata.length - 1].key === '--'
							? 0
							: 1) &&
					scoreRows.find(
						row =>
							row.value ===
							(requestedCountMetadata?.[0].key ?? ''),
					)))
		) {
			const rows = updateRiskCounts(
				scoreRows,
				riskCounts,
				system === 'Metric',
				isUnique,
			);

			dispatch(setRiskScoreDataGrid(rows));
		}
	}, [riskCounts]);

	useEffect(() => {
		const metaDataName =
			type === ComponentType.Task
				? `${field}-${type}-${uploadId}`
				: `${field}-${type}-${dataSource}`;
		if (
			(isNew || unitMetadata === undefined) &&
			metadataStore &&
			!metadataStore.isLoading &&
			(fetchingMetadata || type === ComponentType.Simulation)
		) {
			// We will get this back for saved muili param components if the user swaps back but we dont need to reset the data just need the units
			if (
				isNew &&
				(component.name.length === 0 ||
					unitMetadataName !== metaDataName)
			) {
				if (
					(metadataStore.unique && metadataStore.unique.length) ||
					metadataStore.range
				) {
					setUnitMetadata({
						unique: metadataStore.unique,
						precision: metadataStore.precision,
						range: metadataStore.range,
						unit: metadataStore.unit,
						currency: metadataStore.currency,
					});
					setUnitMetadataName(metaDataName);

					if (
						metadataStore.unique &&
						metadataStore.unique.length > 0
					) {
						// Convert the default settings into the component version
						let newMetadata = metadataStore.unique.map(item => ({
							key: item,
							value: 0,
						}));

						// Add the out of range if required
						if (
							inspectionType === undefined &&
							newMetadata.find(value => value.key === '--') ===
								undefined
						) {
							newMetadata = [
								{ key: '--', value: 0 },
								...newMetadata,
							];
						}

						setRangeChanged(true);
						setMetadata(newMetadata);
						setMetadataType('unique');
						setNullScore(0);
						createScoreRowsFromComponent(
							true,
							true,
							'unique',
							0,
							newMetadata,
							metadataStore,
						);
					} else if (metadataStore.range) {
						if (
							isNew ||
							(metadata && metadata.length - 2 != rangeClasses)
						) {
							const newMetadata = createDefaultRangeValues(
								rangeClasses,
								metadataStore,
							);
							setRangeChanged(true);
							createScoreRowsFromComponent(
								true,
								true,
								'range',
								0,
								newMetadata,
								metadataStore,
							);
						}
					}
				} else {
					// There is an issue with the data, possibly too many unique values
					setValidConfig(false);
				}
			} else {
				setUnitMetadata({
					unique: metadataStore.unique,
					precision: metadataStore.precision,
					range: metadataStore.range,
					unit: metadataStore.unit,
					currency: metadataStore.currency,
				});
				setUnitMetadataName(metaDataName);
				setIsUnique(metadataType === 'unique');
				setShowScoreTable(true);
			}
			setFetchingMetadata(false);
			setDisableFieldSelect(false);
		}
	}, [metadataStore]);

	useEffect(() => {
		if (isNew && type === ComponentType.Observation && defects?.length) {
			if (!componentChanged) {
				setDisableFieldSelect(false);
				return;
			}
			const newMetadata = defects.map(code => {
				return { key: `${code}`, value: 0 };
			});
			setMetadata(newMetadata);
			setMetadataType('unique');
			setNullScore(0);
			setDisableFieldSelect(false);
			createScoreRowsFromComponent(
				true,
				false,
				'unique',
				0,
				newMetadata,
				metadataStore,
			);
		}
	}, [defects]);

	const onRangeValuesChanged = () => {
		setRangeChanged(true);
		setComponentChanged(true);
		if (onChange) onChange(true, false, false);
	};

	const onSetRangeClasses = (classes: number) => {
		setRangeChanged(true);
		setComponentChanged(true);
		if (onChange) onChange(true, false, false);
		setRangeClasses(classes);
		const newMetadata = createDefaultRangeValues(classes, unitMetadata);
		createScoreRowsFromComponent(
			false,
			true,
			'range',
			0,
			newMetadata,
			unitMetadata,
		);
	};

	const onUpdateCreate = () => {
		// Use the scoreMatrix and the multiScoreEdit to creat the scoreMatrix for the compoent
		if (
			parameterNumber === undefined &&
			multiScoreEdit.length !== 0 &&
			component.scoreMatrix &&
			componentParameter1 &&
			componentParameter1.metadata &&
			componentParameter2 &&
			componentParameter2.metadata
		) {
			let index = 0;
			for (let i = 0; i < componentParameter2.metadata.length; i++) {
				for (
					let i2 = 0;
					i2 < componentParameter1.metadata.length;
					i2++
				) {
					component.scoreMatrix[index].score =
						(multiScoreEdit[i][`${i2}-score`] as number) ?? 0;
					index++;
				}
			}
		}

		if (forLoF && category) {
			const originalLofName = component.name;
			updateComponent();
			if (isNew) {
				dispatch(
					addLoFComponent({
						categoryName: category.name,
						component: component,
					}),
				);
			} else {
				dispatch(
					setLoFComponent({
						categoryName: category.name,
						componentName: originalLofName,
						component: component,
					}),
				);
			}
			if (onApply) onApply(category);
		} else if (!forLoF && category) {
			const originalCofName = component.name;
			updateComponent();
			if (isNew) {
				dispatch(
					addCoFComponent({
						categoryName: category.name,
						component: component,
					}),
				);
			} else {
				dispatch(
					setCoFComponent({
						categoryName: category.name,
						componentName: originalCofName,
						component: component,
					}),
				);
			}
			if (onApply) onApply(undefined);
		}
	};

	const onComponentChanged = (
		changed: boolean,
		clearMatrix: boolean,
		nextTab: boolean,
	) => {
		setComponentChanged(changed);
		if (clearMatrix) setComponentMatrix(undefined);
		if (nextTab) setCurrentIndex(currentIndex + 1);
	};

	const mapInsert = (
		<Grid xs={4} item paddingLeft={'20px'}>
			<InfoCard title={t('Map')} applyBodyPadding={false}>
				<div className={classes.map}>
					<MapInsertSpatial
						layerName={dataSource}
						showSystemTypeLayers={true}
						logLevel={MapStatusType.WARN}
					/>
				</div>
			</InfoCard>
		</Grid>
	);

	const title = useMemo(() => {
		if (parameterNumber === 1) return t('Parameter 1 Setup');
		else if (parameterNumber === 2) return t('Parameter 2 Setup');
		else if (isNew) return t('Add New Component');
		else return t('Edit Component');
	}, [parameterNumber, isNew]);

	const secondary = () => {
		if (parameterNumber === undefined)
			return (
				<div>
					<Button
						variant="contained"
						color="secondary"
						style={{ marginRight: 16 }}
						onClick={() => {
							if (onCancel) onCancel();
						}}>
						{t('Cancel')}
					</Button>
					<Button
						variant="contained"
						color="primary"
						disabled={
							!validConfig ||
							componentName.length === 0 ||
							validName.length !== 0 ||
							isRiskCountLoading ||
							fetchingMetadata
						}
						onClick={onUpdateCreate}>
						{isNew ? t('Create') : t('Update')}
					</Button>
				</div>
			);
	};

	const parameter1 = () => {
		if (componentParameter1) {
			return (
				<Stack paddingTop={'20px'}>
					<AddEditComponent
						key={'param1'}
						countKey={isNew ? 'NewConfig1' : `${countKey}-param1`}
						forLoF={forLoF}
						observations={observations}
						inspectionType={inspectionType}
						isNew={isNew}
						assetType={assetType}
						category={category}
						component={componentParameter1}
						parentComponent={component}
						parameterNumber={1}
						onApply={() => {
							// Not Needed
						}}
						onCancel={() => {
							// Not Needed
						}}
						onChange={onComponentChanged}
					/>
				</Stack>
			);
		}
	};

	const parameter2 = () => {
		if (componentParameter2) {
			return (
				<Stack paddingTop={'20px'}>
					<AddEditComponent
						key={'param2'}
						countKey={isNew ? 'NewConfig2' : `${countKey}-param2`}
						forLoF={forLoF}
						observations={observations}
						inspectionType={inspectionType}
						isNew={isNew}
						assetType={assetType}
						category={category}
						component={componentParameter2}
						parentComponent={component}
						parameterNumber={2}
						onApply={() => {
							// Not Needed
						}}
						onCancel={() => {
							// Not Needed
						}}
						onChange={onComponentChanged}
					/>
				</Stack>
			);
		}
	};

	const score = () => {
		return (
			<Stack paddingTop={'20px'} spacing={'20px'}>
				<ParameterDetails
					componentParameter1={componentParameter1}
					componentParameter2={componentParameter2}
				/>
				<MultiScoreDataGrid
					dataGridKey={countKey}
					componentParameter1={componentParameter1}
					componentParameter2={componentParameter2}
					componentMatrix={componentMatrix}
					onRecalculateCount={onRecalculateCountMatrix}
				/>
			</Stack>
		);
	};

	const beforeChangeCurrentStep = (event: CancellableEvent) => {
		if (componentChanged) {
			setMoveToIndex(event.toIndex);
			return { ...event, cancelled: true };
		} else {
			setCurrentIndex(event.toIndex);
			return { ...event, cancelled: false };
		}
	};

	const onKeepChanges = () => {
		setMoveToIndex(undefined);
	};

	const onLoseChanges = () => {
		if (moveToIndex !== undefined) {
			setCurrentIndex(moveToIndex);
		}
		setMoveToIndex(undefined);
		setComponentChanged(false);
		dispatch(
			getRiskCountResolved({
				key: changedRangesKey,
				values: undefined,
			}),
		);
	};

	useEffect(() => {
		if (currentIndex === 2) {
			updateComponentMatrix();

			if (
				componentParameter1 &&
				componentParameter1.metadata &&
				componentParameter2 &&
				componentParameter2.metadata
			)
				setValidConfig(true);
		}
	}, [currentIndex]);

	const getComponentCount = (component: Component): ComponentCount => {
		let type = component.type;
		if (inspectionType && !observations) type = 'inspection';
		else if (type === ComponentType.Observation) type = 'observation';
		else if (type === ComponentType.SpatialProximity) type = 'proximity';
		else if (type === ComponentType.SpatialAttribute)
			type = 'proximityAttribute';

		return {
			name: component.name,
			type: type,
			field: component.field,
			layer: component.layer,
			uploadId: component.uploadId,
			maxDistance: component.maxDistance,
			metadataType: component.metadataType,
		};
	};

	const updateComponentMatrix = () => {
		if (
			componentParameter1 &&
			componentParameter1.metadata &&
			componentParameter2 &&
			componentParameter2.metadata &&
			componentMatrix === undefined
		) {
			const matrix: MultiScoreItem[] = [];
			const scoreMatrix: MultiParamCell[] = [];
			const scoreComponents: ComponentCount[] = [
				getComponentCount(componentParameter1),
				getComponentCount(componentParameter2),
			];
			const existingScoreMatix =
				isNew ||
				(component.scoreMatrix &&
					componentParameter2.metadata.length *
						componentParameter1.metadata.length !==
						component.scoreMatrix.length)
					? undefined
					: component.scoreMatrix;
			let index = 0;
			for (let i = 0; i < componentParameter2.metadata.length; i++) {
				const row: MultiScoreItem = {
					id: i,
					param2: componentParameter2.metadata[i].key,
				};

				for (
					let i2 = 0;
					i2 < componentParameter1.metadata.length;
					i2++
				) {
					const score = existingScoreMatix
						? existingScoreMatix[index++].score
						: 0;

					row[`${i2}-score`] = score;
					row[`${i2}-count`] = 0;

					scoreMatrix.push({
						params: [
							{
								index: 0,
								meta: {
									key: componentParameter1.metadata[i2].key,
									ix: i2,
									value: 0,
								},
							},
							{
								index: 1,
								meta: {
									key: componentParameter2.metadata[i].key,
									ix: i,
									value: 0,
								},
							},
						],
						score: score,
					});
				}

				matrix.push(row);
			}

			setComponentMatrix(matrix);
			component.scoreMatrix = scoreMatrix;

			// Get the counts for the matrix
			const key = 'ScoreMatrix';

			if (isWebSocketEnabled) {
				dispatch(
					setRiskCountIsLoading({
						key: key,
						loading: true,
					}),
				);
				webSocket.sendMessage(
					JSON.stringify({
						path: 'risk',
						action: 'riskCalcCount',
						key: key,
						fieldType: 'multiParam',
						scoreMatrix: scoreMatrix,
						assetType: assetType,
						inspectionType: inspectionType,
						paramComponents: scoreComponents,
					}),
				);
			} else {
				dispatch(
					getRiskCountScoreMatrix({
						key: key,
						scoreMatrix: scoreMatrix,
						assetType: assetType,
						inspectionType: inspectionType,
						components: scoreComponents,
					}),
				);
			}
		}
	};

	return (
		<FullPageWrapper
			key={countKey}
			title={title}
			applyPadding={parameterNumber === undefined}
			headerUnderline={parameterNumber === undefined}
			className={
				metadataStore.isLoading ||
				fetchingMetadata ||
				isRiskCountLoading
					? classes.headerWait
					: classes.header
			}
			secondary={secondary()}>
			<FullPage>
				<Grid container>
					<Grid item xs={2}>
						<FormControl
							variant="outlined"
							margin="normal"
							fullWidth>
							<TextField
								fullWidth
								key={parameterNumber}
								name="ComponentName"
								defaultValue={componentName}
								label={
									parameterNumber === undefined
										? t('Component Name')
										: t('Parameter Name')
								}
								variant="outlined"
								onChange={validateName}
								helperText={validName}
								error={validName.length != 0}
							/>
						</FormControl>
					</Grid>
					{componentSetup()}
				</Grid>
				{showScoreTable && (
					<div>
						<Grid container paddingTop={'20px'}>
							<Grid item xs={hasMap ? 8 : 12}>
								<ScoreDataGrid
									forComponent={parameterNumber === undefined}
									forInspection={
										!observations &&
										inspectionType !== undefined
									}
									isLoading={metadataStore?.isLoading}
									isLoadingCount={isRiskCountLoading}
									dataGridKey={countKey}
									countKey={
										component.name.length !== 0 &&
										rangeChanged
											? changedRangesKey
											: countKey
									}
									isUnique={isUnique}
									isNew={isNew}
									rangeClasses={rangeClasses}
									distance={
										type === ComponentType.SpatialAttribute
											? distance
											: undefined
									}
									unit={unitMetadata?.unit}
									currency={unitMetadata?.currency}
									precision={unitMetadata?.precision}
									hideCounts={
										type === ComponentType.Simulation ||
										type === ComponentType.Observation
									}
									onSetRangeClasses={onSetRangeClasses}
									onRangeValuesChanged={onRangeValuesChanged}
									onRecalculateCount={onRecalculateCount}
								/>
							</Grid>
							{hasMap ? mapInsert : []}
						</Grid>
					</div>
				)}
				{showMutliParam && (
					<Stack paddingTop={'20px'}>
						<SecondaryNav
							itemIndex={currentIndex}
							beforeItemIndexChanged={beforeChangeCurrentStep}
							items={[
								{
									title: t('Parameter 1'),
									content: parameter1(),
								},
								{
									title: t('Parameter 2'),
									content: parameter2(),
								},
								{
									title: t('Scores'),
									content: score(),
								},
							]}
						/>
						<WarningDialog
							title={t('Lose Changes')}
							open={moveToIndex !== undefined}
							onCancel={onKeepChanges}
							onConfirm={onLoseChanges}
							confirmText={t('OK')}
							cancelText={t('Cancel')}>
							{t('This will lose any changes made.')}
						</WarningDialog>
					</Stack>
				)}
			</FullPage>
		</FullPageWrapper>
	);
};
